import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  IconButton,
  InputLabel,
  ListItemIcon,
  Menu,
  MenuItem,
  Divider,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import MemoryIcon from '@mui/icons-material/Memory';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PushPinIcon from '@mui/icons-material/PushPin';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import { setLogOut } from '../store/auth/reducer';
import { controller } from '../api/controller';
import { useAdminPanelTheme } from '../project-modules/store/AdminPanelThemeContext';
import { usePublicUiLocale } from '../contexts/PublicUiLocaleContext';
import SuperAdminTopBar from './superadmin-shell/SuperAdminTopBar';
import { getUserFromToken } from './superadmin-shell/superAdminSession';
import {
  addonModulesForPlan,
  isPlanBundledAddon,
} from '../project-modules/planAddonModules';
import {
  parseProjectNameTakenError,
  projectNameTakenMessage,
} from '../project-modules/projectNameHelpers';
import { isUidNameConflictError, uidNameConflictMessage } from '../project-modules/uidNameConflictHelpers';
import UidRenameAttachDialog from './superadmin-shell/UidRenameAttachDialog';
import RenameProjectDialog from './superadmin-shell/RenameProjectDialog';
import RenameUidDialog from './superadmin-shell/RenameUidDialog';
import CabinetProjectInfoDialog from './superadmin-shell/CabinetProjectInfoDialog';
import { fetchBillingAccountSummary, quoteProjectPurchase, quoteUidPurchase } from '../api/billing';
import PurchaseInvoiceDialog from './superadmin-shell/PurchaseInvoiceDialog';
import {
  loadPinnedProjectIds,
  prunePinnedProjectIds,
  savePinnedProjectIds,
  sortProjectsWithPins,
  togglePinnedProjectId,
} from './superadmin-shell/pinnedProjectsStorage';

function formatCount(template, count) {
  return String(template || '{count}').replace('{count}', String(count));
}

function formatDeleteProjectConfirm(template, name) {
  return String(template || '{name}').replace('{name}', String(name));
}

function SuperAdminHomePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sa } = usePublicUiLocale();
  const { ui, listItemPaperSx } = useAdminPanelTheme();
  const session = useMemo(() => getUserFromToken(), []);
  const superAdminId = session?.id ? Number(session.id) : null;

  const purchasePlans = useMemo(
    () => [
      { code: 'starter', name: 'Starter', hint: sa.planStarterHint },
      { code: 'pro', name: 'Pro', hint: sa.planProHint },
      { code: 'enterprise', name: 'Enterprise', hint: sa.planEnterpriseHint },
    ],
    [sa],
  );

  const addonModules = useMemo(
    () => [
      { code: 'trends', label: sa.addonTrends },
      { code: 'telegram', label: sa.addonTelegram },
      { code: 'telegram_advanced', label: sa.addonTelegramAdvanced },
      { code: 'svg_hmi', label: sa.addonSvgHmi },
      { code: 'project_tags', label: sa.addonProjectTags },
      { code: 'project_alarm_rules', label: sa.addonAlarmRules },
      { code: 'commands_advanced', label: sa.addonCommandsAdvanced },
      { code: 'maps', label: sa.addonMaps },
      { code: 'debug_tools', label: sa.addonDebugTools },
    ],
    [sa],
  );

  const [projects, setProjects] = useState([]);
  const [assignedUids, setAssignedUids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [uidDialogOpen, setUidDialogOpen] = useState(false);
  const [deletingProject, setDeletingProject] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuProject, setMenuProject] = useState(null);
  const [pinnedIds, setPinnedIds] = useState(() => loadPinnedProjectIds(superAdminId));
  const [renameAttach, setRenameAttach] = useState(null);
  const [infoProject, setInfoProject] = useState(null);
  const [renamingProject, setRenamingProject] = useState(null);
  const [renamingUid, setRenamingUid] = useState(null);
  const [uidMenuAnchor, setUidMenuAnchor] = useState(null);
  const [uidMenuTarget, setUidMenuTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [nameTaken, setNameTaken] = useState(false);
  const [checkingName, setCheckingName] = useState(false);
  const [billingSummary, setBillingSummary] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [purchaseQuote, setPurchaseQuote] = useState(null);
  const [purchaseQuoteKind, setPurchaseQuoteKind] = useState(null);
  const [purchaseQuoteOpen, setPurchaseQuoteOpen] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const [projectForm, setProjectForm] = useState({
    slug: '',
    name: '',
    plan_code: 'starter',
    addon_modules: [],
    uid_names: [''],
  });

  const [uidForm, setUidForm] = useState({
    name: '',
    attach_to_project_slug: '',
  });

  const ownedProjects = useMemo(
    () => projects.filter((p) => Number(p.owner_super_admin_id) === Number(superAdminId)),
    [projects, superAdminId],
  );

  const sortedOwnedProjects = useMemo(() => {
    const pruned = prunePinnedProjectIds(pinnedIds, ownedProjects);
    return sortProjectsWithPins(ownedProjects, pruned);
  }, [ownedProjects, pinnedIds]);

  useEffect(() => {
    if (!superAdminId) return;
    const stored = loadPinnedProjectIds(superAdminId);
    const pruned = prunePinnedProjectIds(stored, ownedProjects);
    setPinnedIds(pruned);
    if (pruned.length !== stored.length) {
      savePinnedProjectIds(superAdminId, pruned);
    }
  }, [superAdminId, ownedProjects]);

  const attachedUidCodes = useMemo(() => {
    const codes = new Set();
    projects.forEach((p) => {
      (p.uids || []).forEach((u) => codes.add(String(u.code).toLowerCase()));
    });
    return codes;
  }, [projects]);

  const freeUids = useMemo(
    () =>
      (assignedUids || []).filter(
        (u) => !attachedUidCodes.has(String(u.code || '').toLowerCase()),
      ),
    [assignedUids, attachedUidCodes],
  );

  const billingBySlug = useMemo(() => {
    const map = new Map();
    (billingSummary?.projects || []).forEach((p) => map.set(p.slug, p));
    return map;
  }, [billingSummary]);

  const load = useCallback(async () => {
    if (!superAdminId) return;
    setLoading(true);
    setBillingLoading(true);
    setError('');
    try {
      const [projectsRes, uidsRes, billingData] = await Promise.all([
        controller.post('/catalog/projects/list-with-uids'),
        controller.post('/catalog/uids/list/by-owner', { superAdminId }),
        fetchBillingAccountSummary().catch(() => null),
      ]);
      setProjects(projectsRes.data?.items || []);
      setAssignedUids(uidsRes.data?.items || []);
      setBillingSummary(billingData);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || sa.loadFailed);
    } finally {
      setLoading(false);
      setBillingLoading(false);
    }
  }, [superAdminId, sa.loadFailed]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = () => {
    dispatch(setLogOut());
    navigate('/');
  };

  const openProjectDialog = () => {
    setFormError('');
    setNameSuggestions([]);
    setNameTaken(false);
    setProjectForm({
      slug: '',
      name: '',
      plan_code: 'starter',
      addon_modules: addonModulesForPlan('starter'),
      uid_names: [''],
    });
    setProjectDialogOpen(true);
  };

  const handlePlanChange = (planCode) => {
    setProjectForm((f) => ({
      ...f,
      plan_code: planCode,
      addon_modules: addonModulesForPlan(planCode),
    }));
  };

  useEffect(() => {
    if (!projectDialogOpen) return undefined;
    const trimmed = projectForm.name.trim();
    if (!trimmed) {
      setNameTaken(false);
      setNameSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setCheckingName(true);
      try {
        const res = await controller.get(
          `/catalog/projects/suggest-name?name=${encodeURIComponent(trimmed)}`,
        );
        if (cancelled) return;
        const taken = !!res.data?.taken;
        const suggestions = Array.isArray(res.data?.suggestions)
          ? res.data.suggestions.filter((s) => typeof s === 'string')
          : [];
        setNameTaken(taken);
        setNameSuggestions(taken ? suggestions : []);
      } catch {
        if (!cancelled) {
          setNameTaken(false);
          setNameSuggestions([]);
        }
      } finally {
        if (!cancelled) setCheckingName(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [projectDialogOpen, projectForm.name]);

  const purchaseInvoiceLocale = useMemo(
    () => ({
      invoiceIntro: sa.purchaseInvoiceIntro,
      chargeNowSection: sa.purchaseInvoiceChargeNow,
      monthlySection: sa.purchaseInvoiceMonthly,
      chargeNowTotal: sa.purchaseInvoiceChargeNowTotal,
      monthlyRecurring: sa.purchaseInvoiceMonthlyRecurring,
      monthlyRecurringHint: sa.purchaseInvoiceMonthlyHint,
      balanceAfter: sa.purchaseInvoiceBalanceAfter,
      balanceCurrent: sa.purchaseInvoiceBalanceCurrent,
      availableCredit: sa.purchaseInvoiceAvailable,
      insufficientBalance: sa.purchaseInvoiceInsufficient,
      confirmPay: sa.purchaseInvoiceConfirm,
      cancel: sa.cancel,
    }),
    [sa],
  );

  const requestProjectQuote = async () => {
    const uid_names = projectForm.uid_names.map((n) => n.trim()).filter(Boolean);
    const lowered = uid_names.map((n) => n.toLowerCase());
    if (new Set(lowered).size !== lowered.length) {
      setFormError(sa.uidNameConflict);
      return;
    }
    setQuoteLoading(true);
    setFormError('');
    try {
      const quote = await quoteProjectPurchase({
        plan_code: projectForm.plan_code,
        addon_modules: projectForm.addon_modules,
      });
      setPurchaseQuote(quote);
      setPurchaseQuoteKind('project');
      setPurchaseQuoteOpen(true);
    } catch (e) {
      setFormError(e?.response?.data?.message || e?.message || sa.createFailed);
    } finally {
      setQuoteLoading(false);
    }
  };

  const confirmPurchaseProject = async () => {
    setSubmitting(true);
    setFormError('');
    try {
      const uid_names = projectForm.uid_names.map((n) => n.trim()).filter(Boolean);
      const res = await controller.post('/catalog/purchase/project', {
        slug: projectForm.slug.trim().toLowerCase(),
        name: projectForm.name.trim(),
        plan_code: projectForm.plan_code,
        addon_modules: projectForm.addon_modules,
        uid_names,
      });
      setPurchaseQuoteOpen(false);
      setProjectDialogOpen(false);
      await load();
      const slug = res.data?.slug;
      if (slug) {
        navigate(`/v2/superadmin/project/${encodeURIComponent(slug)}`);
      }
    } catch (e) {
      const suggestions = parseProjectNameTakenError(e);
      if (suggestions.length) {
        setNameSuggestions(suggestions);
        setNameTaken(true);
      }
      setFormError(
        projectNameTakenMessage(e, e?.response?.data?.message || e?.message || sa.createFailed),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const requestUidQuote = async () => {
    if (!uidForm.name.trim()) {
      setFormError(sa.createFailed);
      return;
    }
    setQuoteLoading(true);
    setFormError('');
    try {
      const quote = await quoteUidPurchase();
      setPurchaseQuote(quote);
      setPurchaseQuoteKind('uid');
      setPurchaseQuoteOpen(true);
    } catch (e) {
      setFormError(e?.response?.data?.message || e?.message || sa.createFailed);
    } finally {
      setQuoteLoading(false);
    }
  };

  const confirmPurchaseUid = async () => {
    setSubmitting(true);
    setFormError('');
    try {
      await controller.post('/catalog/purchase/uid', {
        name: uidForm.name.trim(),
        attach_to_project_slug: uidForm.attach_to_project_slug || undefined,
      });
      setPurchaseQuoteOpen(false);
      setUidDialogOpen(false);
      setUidForm({ name: '', attach_to_project_slug: '' });
      await load();
    } catch (e) {
      setFormError(
        isUidNameConflictError(e)
          ? uidNameConflictMessage(e, sa.uidNameConflict)
          : (e?.response?.data?.message || e?.message || sa.createFailed),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const attachFreeUid = async (uidCode, projectSlug) => {
    if (!projectSlug) return;
    const uid = freeUids.find((u) => String(u.code) === String(uidCode));
    try {
      await controller.post('/catalog/projects/uids/link-by-code', {
        projectSlug,
        uidCode,
      });
      await load();
    } catch (e) {
      if (isUidNameConflictError(e) && uid) {
        setRenameAttach({ uid, projectSlug });
        return;
      }
      setError(e?.response?.data?.message || e?.message || sa.attachFailed);
    }
  };

  const handleDeleteProject = async () => {
    if (!deletingProject) return;
    setSubmitting(true);
    setError('');
    try {
      await controller.deleteProject(deletingProject.id, { dropDatabase: true });
      if (superAdminId) {
        const nextPins = pinnedIds.filter((id) => id !== Number(deletingProject.id));
        setPinnedIds(nextPins);
        savePinnedProjectIds(superAdminId, nextPins);
      }
      setDeletingProject(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || sa.deleteProjectFailed);
    } finally {
      setSubmitting(false);
    }
  };

  const closeProjectMenu = () => {
    setMenuAnchor(null);
    setMenuProject(null);
  };

  const handleTogglePin = (project) => {
    if (!superAdminId || !project) return;
    const next = togglePinnedProjectId(superAdminId, project.id, pinnedIds);
    setPinnedIds(next);
    closeProjectMenu();
  };

  const tryOpenProject = (p) => {
    if (!p?.slug) return;
    const bill = billingBySlug.get(p.slug);
    if (bill?.billing_suspended) {
      setError(sa.billingProjectSuspended);
      return;
    }
    navigate(`/v2/superadmin/project/${encodeURIComponent(p.slug)}`);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: ui.pageBg || 'background.default', color: ui.textPrimary, p: { xs: 1.5, sm: 2.5 } }}>
      <SuperAdminTopBar
        title={sa.cabinetTitle}
        username={session?.username}
        role={session?.role}
        onLogout={handleLogout}
        billingSummary={billingSummary}
        billingLoading={billingLoading}
        fetchBillingInternally={false}
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
        <Chip icon={<FolderOpenIcon />} label={formatCount(sa.projectsCount, ownedProjects.length)} variant="outlined" sx={{ borderColor: ui.border }} />
        <Chip icon={<MemoryIcon />} label={formatCount(sa.freeUidsCount, freeUids.length)} variant="outlined" sx={{ borderColor: ui.border }} />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} alignItems="flex-start">
          <Paper
            data-help="cabinet-projects"
            elevation={0}
            sx={{
              flex: 1,
              width: '100%',
              bgcolor: ui.cardBg,
              border: ui.border,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: ui.border, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ color: ui.textPrimary, fontWeight: 600 }}>
                {sa.myProjects}
              </Typography>
              <Button size="small" variant="text" startIcon={<AddIcon />} onClick={openProjectDialog}>
                {sa.addProject}
              </Button>
            </Box>

            {ownedProjects.length === 0 ? (
              <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">{sa.emptyCabinet}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                  {sa.emptyCabinetHint}
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openProjectDialog}>
                    {sa.addProject}
                  </Button>
                  <Button size="small" variant="text" onClick={() => { setFormError(''); setUidDialogOpen(true); }}>
                    {sa.addUid}
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: ui.textSecondary, borderColor: ui.dividerColor }}>{sa.colName}</TableCell>
                    <TableCell sx={{ color: ui.textSecondary, borderColor: ui.dividerColor }}>{sa.colPlan}</TableCell>
                    <TableCell sx={{ color: ui.textSecondary, borderColor: ui.dividerColor }} align="center">UID</TableCell>
                    <TableCell align="right" sx={{ color: ui.textSecondary, borderColor: ui.dividerColor }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedOwnedProjects.map((p, projectIndex) => {
                    const isPinned = pinnedIds.includes(Number(p.id));
                    const bill = billingBySlug.get(p.slug);
                    const suspended = Boolean(bill?.billing_suspended);
                    const renewalWarn = Boolean(bill?.insufficient_for_renewal && !suspended);
                    return (
                    <TableRow
                      key={p.id}
                      data-help={projectIndex === 0 ? 'project-row' : undefined}
                      data-slug={projectIndex === 0 ? p.slug : undefined}
                      hover
                      sx={{
                        cursor: suspended ? 'not-allowed' : 'pointer',
                        opacity: suspended ? 0.65 : 1,
                        '&:last-child td': { border: 0 },
                      }}
                      onClick={() => tryOpenProject(p)}
                    >
                      <TableCell sx={{ borderColor: ui.dividerColor }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          {isPinned ? (
                            <PushPinIcon sx={{ fontSize: 14, color: ui.textSecondary, flexShrink: 0 }} />
                          ) : null}
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="body2" fontWeight={600} noWrap>{p.name}</Typography>
                              {suspended ? (
                                <Chip size="small" color="error" icon={<BlockIcon />} label={sa.billingSuspendedBadge} />
                              ) : renewalWarn ? (
                                <Chip size="small" color="warning" icon={<WarningAmberIcon />} label={sa.billingRenewalBadge} />
                              ) : null}
                            </Stack>
                            <Typography variant="caption" sx={{ color: ui.textSecondary }} noWrap>{p.slug}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ borderColor: ui.dividerColor }}>
                        <Chip size="small" label={p.plan_code || 'starter'} />
                      </TableCell>
                      <TableCell align="center" sx={{ borderColor: ui.dividerColor }}>
                        {(p.uids || []).length}
                      </TableCell>
                      <TableCell align="right" sx={{ borderColor: ui.dividerColor, width: 48 }} onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          aria-label="actions"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuAnchor(e.currentTarget);
                            setMenuProject(p);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>

          <Paper
            data-help="cabinet-uids"
            elevation={0}
            sx={{
              width: { xs: '100%', lg: 320 },
              flexShrink: 0,
              bgcolor: ui.cardBg,
              border: ui.border,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: ui.border, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ color: ui.textPrimary, fontWeight: 600 }}>
                {sa.freeUidsTitle}
              </Typography>
              <Button size="small" variant="text" startIcon={<AddIcon />} onClick={() => { setFormError(''); setUidDialogOpen(true); }}>
                {sa.addUid}
              </Button>
            </Box>
            <Box sx={{ p: 2 }}>
              {freeUids.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {sa.allUidsAttached}
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {freeUids.map((u) => (
                    <Box
                      key={u.id}
                      sx={{
                        ...listItemPaperSx(false),
                        p: 1.5,
                        mb: 0,
                        cursor: 'default',
                        position: 'relative',
                      }}
                    >
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: 4, right: 4 }}
                        onClick={(e) => {
                          setUidMenuAnchor(e.currentTarget);
                          setUidMenuTarget(u);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ pr: 3 }}>{u.name}</Typography>
                      <Typography variant="caption" sx={{ color: ui.textSecondary, display: 'block', mb: 1 }} noWrap>
                        {u.code}
                      </Typography>
                      {ownedProjects.length > 0 ? (
                        <FormControl size="small" fullWidth>
                          <Select
                            displayEmpty
                            value=""
                            renderValue={() => sa.attachTo}
                            onChange={(e) => attachFreeUid(u.code, e.target.value)}
                            sx={{ fontSize: 13 }}
                          >
                            {sortedOwnedProjects.map((p) => (
                              <MenuItem key={p.id} value={p.slug}>{p.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Paper>
        </Stack>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor && menuProject)}
        onClose={closeProjectMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            tryOpenProject(menuProject);
            closeProjectMenu();
          }}
          disabled={Boolean(billingBySlug.get(menuProject?.slug)?.billing_suspended)}
        >
          <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
          {sa.openProject}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setInfoProject(menuProject);
            closeProjectMenu();
          }}
        >
          <ListItemIcon><InfoOutlinedIcon fontSize="small" /></ListItemIcon>
          {sa.cabinetViewProjectInfo}
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate(`/p/${encodeURIComponent(menuProject.slug)}/store`);
            closeProjectMenu();
          }}
        >
          <ListItemIcon><StorefrontIcon fontSize="small" /></ListItemIcon>
          {sa.projectStore}
        </MenuItem>
        <MenuItem onClick={() => handleTogglePin(menuProject)}>
          <ListItemIcon><PushPinIcon fontSize="small" /></ListItemIcon>
          {pinnedIds.includes(Number(menuProject?.id)) ? sa.unpinProject : sa.pinProject}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setRenamingProject(menuProject);
            closeProjectMenu();
          }}
        >
          <ListItemIcon><DriveFileRenameOutlineIcon fontSize="small" /></ListItemIcon>
          {sa.renameProject}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setDeletingProject({ ...menuProject });
            closeProjectMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}><DeleteIcon fontSize="small" /></ListItemIcon>
          {sa.delete}
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={uidMenuAnchor}
        open={Boolean(uidMenuAnchor && uidMenuTarget)}
        onClose={() => { setUidMenuAnchor(null); setUidMenuTarget(null); }}
      >
        <MenuItem
          onClick={() => {
            setRenamingUid(uidMenuTarget);
            setUidMenuAnchor(null);
            setUidMenuTarget(null);
          }}
        >
          <ListItemIcon><DriveFileRenameOutlineIcon fontSize="small" /></ListItemIcon>
          {sa.renameUid}
        </MenuItem>
      </Menu>

      <Dialog open={projectDialogOpen} onClose={() => !submitting && setProjectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{sa.newProjectTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label={sa.slugLabel}
              value={projectForm.slug}
              onChange={(e) => setProjectForm((f) => ({ ...f, slug: e.target.value.toLowerCase() }))}
              helperText={sa.slugHelper}
              fullWidth
              size="small"
            />
            <TextField
              label={sa.name}
              value={projectForm.name}
              onChange={(e) => setProjectForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              size="small"
              error={nameTaken}
              helperText={nameTaken ? sa.projectNameTaken : undefined}
            />
            {nameTaken && nameSuggestions.length > 0 ? (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                  {sa.projectNameSuggestions}
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {nameSuggestions.map((suggestion) => (
                    <Chip
                      key={suggestion}
                      size="small"
                      label={suggestion}
                      clickable
                      variant="outlined"
                      onClick={() => {
                        setProjectForm((f) => ({ ...f, name: suggestion }));
                        setFormError('');
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            ) : null}
            <FormControl fullWidth size="small">
              <InputLabel>{sa.planLabel}</InputLabel>
              <Select
                label={sa.planLabel}
                value={projectForm.plan_code}
                onChange={(e) => handlePlanChange(e.target.value)}
              >
                {purchasePlans.map((pl) => (
                  <MenuItem key={pl.code} value={pl.code}>
                    {pl.name}
                    {' — '}
                    {pl.hint}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormGroup row sx={{ gap: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ width: '100%', mb: 0.5 }}>
                {sa.addonModulesLabel}
              </Typography>
              {addonModules.map((m) => {
                const bundled = isPlanBundledAddon(projectForm.plan_code, m.code);
                return (
                <FormControlLabel
                  key={m.code}
                  control={(
                    <Checkbox
                      size="small"
                      checked={bundled || projectForm.addon_modules.includes(m.code)}
                      disabled={bundled}
                      onChange={(e) => {
                        if (bundled) return;
                        setProjectForm((f) => ({
                          ...f,
                          addon_modules: e.target.checked
                            ? [...f.addon_modules, m.code]
                            : f.addon_modules.filter((c) => c !== m.code),
                        }));
                      }}
                    />
                  )}
                  label={<Typography variant="caption">{m.label}</Typography>}
                />
                );
              })}
            </FormGroup>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {sa.uidOnCreateLabel}
              </Typography>
              {projectForm.uid_names.map((name, idx) => (
                <Stack key={idx} direction="row" spacing={1} sx={{ mb: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={sa.uidRowPlaceholder.replace('{n}', String(idx + 1))}
                    value={name}
                    onChange={(e) => {
                      const next = [...projectForm.uid_names];
                      next[idx] = e.target.value;
                      setProjectForm((f) => ({ ...f, uid_names: next }));
                    }}
                  />
                  {projectForm.uid_names.length > 1 && (
                    <IconButton size="small" onClick={() => setProjectForm((f) => ({
                      ...f,
                      uid_names: f.uid_names.filter((_, i) => i !== idx),
                    }))}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={() => setProjectForm((f) => ({ ...f, uid_names: [...f.uid_names, ''] }))}>
                {sa.addUidRow}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectDialogOpen(false)} disabled={submitting}>{sa.cancel}</Button>
          <Button variant="contained" onClick={requestProjectQuote} disabled={submitting || quoteLoading || nameTaken || checkingName}>
            {submitting ? sa.creating : sa.create}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={uidDialogOpen} onClose={() => !submitting && setUidDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{sa.newUidTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label={sa.name}
              value={uidForm.name}
              onChange={(e) => setUidForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>{sa.projectSelectLabel}</InputLabel>
              <Select
                label={sa.projectSelectLabel}
                value={uidForm.attach_to_project_slug}
                onChange={(e) => setUidForm((f) => ({ ...f, attach_to_project_slug: e.target.value }))}
              >
                <MenuItem value=""><em>{sa.freeUidOption}</em></MenuItem>
                {ownedProjects.map((p) => (
                  <MenuItem key={p.id} value={p.slug}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUidDialogOpen(false)} disabled={submitting}>{sa.cancel}</Button>
          <Button variant="contained" onClick={requestUidQuote} disabled={submitting || quoteLoading}>
            {submitting ? sa.creating : sa.create}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deletingProject} onClose={() => !submitting && setDeletingProject(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{sa.delete}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {formatDeleteProjectConfirm(sa.confirmDeleteProject, deletingProject?.name || deletingProject?.slug || '')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingProject(null)} disabled={submitting}>{sa.cancel}</Button>
          <Button color="error" variant="contained" onClick={handleDeleteProject} disabled={submitting}>
            {submitting ? sa.creating : sa.delete}
          </Button>
        </DialogActions>
      </Dialog>

      <UidRenameAttachDialog
        open={!!renameAttach}
        uid={renameAttach?.uid}
        projectSlug={renameAttach?.projectSlug}
        onClose={() => setRenameAttach(null)}
        onAttached={load}
      />

      <CabinetProjectInfoDialog
        open={!!infoProject}
        project={infoProject}
        billingSummary={billingSummary}
        onClose={() => setInfoProject(null)}
      />

      <RenameProjectDialog
        open={!!renamingProject}
        project={renamingProject}
        onClose={() => setRenamingProject(null)}
        onRenamed={load}
      />

      <RenameUidDialog
        open={!!renamingUid}
        uid={renamingUid}
        onClose={() => setRenamingUid(null)}
        onRenamed={load}
      />

      <PurchaseInvoiceDialog
        open={purchaseQuoteOpen}
        title={
          purchaseQuoteKind === 'uid'
            ? sa.purchaseInvoiceTitleUid
            : sa.purchaseInvoiceTitleProject
        }
        subtitle={
          purchaseQuoteKind === 'project'
            ? projectForm.name || projectForm.slug
            : uidForm.name
        }
        quote={purchaseQuote}
        locale={purchaseInvoiceLocale}
        submitting={submitting}
        onClose={() => {
          setPurchaseQuoteOpen(false);
          setPurchaseQuote(null);
        }}
        onConfirm={
          purchaseQuoteKind === 'uid' ? confirmPurchaseUid : confirmPurchaseProject
        }
      />
    </Box>
  );
}

export default SuperAdminHomePage;
